import React, { useRef, useMemo, useEffect, useCallback, ChangeEvent } from 'react';

import { debounce } from 'lodash';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { KEYS } from '../../../../../../../../constants/Common';
import { WIDTH } from '../../../../../../../../constants/Variables';

import PlaceServices from '../../../../../../../../services/Api/Place';
import Url from '../../../../../../../../services/Url';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../../../hooks/connectAuth';

import Input from '../../../../../Common/Form/Input/Input';
import InputTrailingIcon from '../../../../../Common/Form/Input/InputTrailingIcon';
import { multipleActions } from '../../../../Store/ContainerReducer';
import { IAuthState } from '../../../../Store/Reducers';
import { Types, initialState as reducerInitialState, ICity, IKeyDown } from '../../../../Store/SignUpReducer';


const KEYS_WHITELIST = [
    KEYS.ESC,
    KEYS.TAB,
    KEYS.ENTER,
    KEYS.ARROW_UP,
    KEYS.ARROW_DOWN
];

const initialState = reducerInitialState.SignUp.birthData.birthPlace.cityState;

interface ICitySelectorProps {
    error: string,
    reset: boolean,
    place: ICity,
    cities: ICity[],
    touched: boolean,
    loading: boolean,
    keyDown: IKeyDown,
    hoverItem: number,
    showSugestions: boolean,
    cityDescription: string,
    placeholderBirthPlace?: string,
}

type TCitySelectorProps = IConnectedProps<ICitySelectorProps>;

const Container = styled.div`
    width: 100%;
    display: flex;
    position: relative;
    align-items: center;
    flex-direction: column;
`;

const Sugestions = styled.div<{ $visible: boolean }>`
    z-index: 3;
    width: 100%;
    max-width: 600px;
    position: absolute;
    top: calc(100% - 2px);
    background-size: 144px;
    box-sizing: border-box;
    flex-direction: column;
    background-repeat: no-repeat;
    background-position: bottom right;
    display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
    border: 1px solid ${({ theme }) => theme.colors.gray300};
    background-color: ${({ theme }) => theme.colors.surface};
    background-image: url(${Url.image('powered-by-google.png')});

    > p {
        margin: 0;
        width: 100%;
        display: flex;
        cursor: pointer;
        align-items: center;
        box-sizing: border-box;
        color: ${({ theme }) => theme.colors.gray600};
        font-size: ${({ theme }) => theme.fontSizes.cat};
        padding: ${({ theme }) => theme.space.butterfly};

        &[data-selected = true] {
            color: ${({ theme }) => theme.colors.onPrimary};
            background-color: ${({ theme }) => theme.colors.primary};
        }

        &:hover {
            color: ${({ theme }) => theme.colors.onSurfaceVariant};
            background-color: ${({ theme }) => theme.colors.surfaceVariant};
        }
    }

    > span {
        text-align: center;
        padding: ${({ theme }) => theme.space.bee};
        color: ${({ theme }) => theme.colors.onSurface};
        font-size: ${({ theme }) => theme.fontSizes.butterfly};
        margin-bottom: ${({ theme }) => theme.space.butterfly};

        > strong {
            display: flex;
            cursor: pointer;
            justify-content: center;
            color: ${({ theme }) => theme.colors.onSurfaceVariant};
        }
    }

    @media only screen and (max-width: ${WIDTH.MOBILE.MAX}) {
        > p,
        > span {
            font-size: ${({ theme }) => theme.fontSizes.butterfly};
        }
    }
`;

const CitySelector: React.FC<TCitySelectorProps> = ({ intl, dispatch, ...props }) => {
    const input = useRef<HTMLInputElement>(null);

    const {
        error,
        reset,
        place,
        cities,
        touched,
        keyDown,
        loading,
        hoverItem,
        showSugestions,
        cityDescription,
        placeholderBirthPlace
    } = props;

    const placeholder = placeholderBirthPlace || intl.formatMessage({ id: 'authFunctional.inputCity.placeholder' });

    const $PlaceServices = useMemo(() => new PlaceServices(), []);

    const handleSelectedCity = useCallback((selectedCity: ICity) => {
        multipleActions(dispatch, [
            {
                type: Types.CHANGE_CITY_STATE,
                data: { cityDescription: selectedCity.description }
            },
            {
                type: Types.CHANGE_PLACE_DATA,
                data: {
                    error: '',
                    place: selectedCity
                }
            }
        ]);
    }, [dispatch]);

    const handleBlurCity = useCallback(() => {
        const hasPlaceData = place && !!Object.keys(place).length;
        const hasCityDescription = cityDescription && cityDescription.length >= 3;

        if (!hasPlaceData && !showSugestions && hasCityDescription) {
            dispatch({
                type: Types.CHANGE_PLACE_DATA,
                data: {
                    error: intl.formatMessage({ id: 'authFunctional.errors.placeNotSelected' })
                }
            });
        } else if (!hasCityDescription) {
            dispatch({
                type: Types.CHANGE_PLACE_DATA,
                data: {
                    error: intl.formatMessage({ id: 'authFunctional.emptyField' })
                }
            });
        }
    }, [place, cityDescription, touched, showSugestions, dispatch, intl]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchCities = useCallback(debounce(async (value: string, forceFallback = false) => {
        const cityName = value.trim();

        try {
            const res = await $PlaceServices.getCities(cityName, forceFallback);

            dispatch({
                type: Types.CHANGE_CITY_STATE,
                data: {
                    cities: res,
                    loading: false,
                    showSugestions: true,
                    cityDescription: value
                }
            });
        } catch (err) {
            if (forceFallback) {
                dispatch({
                    type: Types.CHANGE_PLACE_DATA,
                    data: {
                        error: intl.formatMessage({ id: 'authFunctional.errors.placeNotFound' })
                    }
                });
            }

            const inputValue = input.current?.value;
            const currentValue = inputValue !== value ? inputValue : value;

            dispatch({
                type: Types.CHANGE_CITY_STATE,
                data: {
                    cities: [],
                    loading: false,
                    showSugestions: true,
                    cityDescription: currentValue
                }
            });
        }
    }, 250), []);

    const handleNotFoundCity = () => {
        dispatch({
            type: Types.CHANGE_CITY_STATE,
            data: {
                cities: [],
                loading: true
            }
        });

        fetchCities(cityDescription, true);
    };

    const handleSearchCity = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
        if (!target) return;

        if (error || (place && Object.values(place).length)) {
            dispatch({
                type: Types.CHANGE_PLACE_DATA,
                data: {
                    error: '',
                    place: {}
                }
            });
        }

        if (target.value.length <= 2) {
            dispatch({
                type: Types.CHANGE_CITY_STATE,
                data: {
                    touched: true,
                    loading: false,
                    showSugestions: false,
                    cityDescription: target.value
                }
            });

            return;
        }

        dispatch({
            type: Types.CHANGE_CITY_STATE,
            data: {
                loading: true,
                cityDescription: target.value
            }
        });

        fetchCities(target.value);
    }, [place, error, dispatch, fetchCities]);


    const handleOnKeyDown = useCallback(({ key }: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSugestions || !cities.length || !key) return;

        const actions = {
            [KEYS.ARROW_UP]: () => {
                if (input.current) input.current.focus();

                dispatch({
                    type: Types.CHANGE_CITY_STATE,
                    data: { hoverItem: hoverItem === 0 ? 0 : hoverItem - 1 }
                });
            },

            [KEYS.ARROW_DOWN]: () => {
                if (input.current) input.current.focus();

                dispatch({
                    type: Types.CHANGE_CITY_STATE,
                    data: { hoverItem: hoverItem === (cities.length - 1) ? hoverItem : hoverItem + 1 }
                });
            },

            [KEYS.ENTER]: () => {
                handleSelectedCity(cities[hoverItem]);
            },

            [KEYS.ESC]: () => {
                dispatch({
                    type: Types.CHANGE_CITY_STATE,
                    data: { showSugestions: false }
                });
            },

            [KEYS.TAB]: () => {
                handleSelectedCity(cities[0]);
            }
        };

        if (!actions[key]) return;

        actions[key]();
    }, [cities, dispatch, handleSelectedCity, hoverItem, showSugestions]);

    useEffect(() => {
        const isArrow = keyDown.key === KEYS.ARROW_UP || keyDown.key === KEYS.ARROW_DOWN;

        if (!(showSugestions && isArrow && input.current)) return;

        input.current.focus();
    }, [keyDown, showSugestions]);

    useEffect(() => {
        if (!place || !Object.keys(place).length) return;

        dispatch({
            type: Types.CHANGE_CITY_STATE,
            data: { showSugestions: false }
        });
    }, [place, dispatch]);

    useEffect(() => {
        if (!reset || !place || place.description === cityDescription) return;

        dispatch({
            type: Types.CHANGE_CITY_STATE,
            data: initialState
        });
    }, [place, reset, cityDescription, dispatch]);

    useEffect(() => {
        if (!place || !Object.values(place).length) return;

        dispatch({
            type: Types.CHANGE_CITY_STATE,
            data: { cityDescription: place.description }
        });
    }, [place, dispatch]);

    return (
        <Container>
            <Input
                order={1}
                ref={input}
                error={error}
                id="birth-city"
                trailingTabIndex={-1}
                value={cityDescription}
                onBlur={handleBlurCity}
                placeholder={placeholder}
                onKeyDown={handleOnKeyDown}
                onChange={handleSearchCity}
            >
                <InputTrailingIcon
                    width={24}
                    height={24}
                    loading={!!loading}
                    data-cy="input-birth-city-icon"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.1818 0C7.65844 0 4 3.756 4 8.4C4 14.7 12.1818 24 12.1818 24C12.1818 24 20.3636 14.7 20.3636 8.4C20.3636 3.756 16.7052 0 12.1818 0ZM12.1818 11.4C11.4068 11.4 10.6636 11.0839 10.1156 10.5213C9.5676 9.95871 9.25974 9.19565 9.25974 8.4C9.25974 7.60435 9.5676 6.84129 10.1156 6.27868C10.6636 5.71607 11.4068 5.4 12.1818 5.4C12.9568 5.4 13.7 5.71607 14.248 6.27868C14.796 6.84129 15.1039 7.60435 15.1039 8.4C15.1039 9.19565 14.796 9.95871 14.248 10.5213C13.7 11.0839 12.9568 11.4 12.1818 11.4Z"
                    />
                </InputTrailingIcon>
            </Input>

            <Sugestions $visible={showSugestions} data-cy="birth-city-sugestions">
                {cities.map((item, id) => (
                    <p
                        key={item.place_id}
                        data-selected={id === hoverItem}
                        onClick={() => handleSelectedCity(item)}
                    >
                        {item.description}
                    </p>
                ))}

                <FormattedMessage id="authFunctional.inputCity.sugestion.span">
                    {(span: string) => (
                        <span data-cy="city-not-found">
                            {span}

                            <FormattedMessage id="authFunctional.inputCity.sugestion.strong">
                                {(strong: string) => <strong onClick={handleNotFoundCity}>{strong}</strong>}
                            </FormattedMessage>
                        </span>
                    )}
                </FormattedMessage>
            </Sugestions>
        </Container>
    );
};

const mapStateToProps: IAuthMapStateToProps<ICitySelectorProps> = (state, isComparison = false) => ((!isComparison)
    ? {
        reset: (state as IAuthState).Container.reset,
        error: (state as IAuthState).SignUp.birthData.birthPlace.error,
        place: (state as IAuthState).SignUp.birthData.birthPlace.place,
        cities: (state as IAuthState).SignUp.birthData.birthPlace.cityState.cities,
        touched: (state as IAuthState).SignUp.birthData.birthPlace.cityState.touched,
        loading: (state as IAuthState).SignUp.birthData.birthPlace.cityState.loading,
        hoverItem: (state as IAuthState).SignUp.birthData.birthPlace.cityState.hoverItem,
        placeholderBirthPlace: (state as IAuthState).Container.communication.placeholderBirthPlace,
        showSugestions: (state as IAuthState).SignUp.birthData.birthPlace.cityState.showSugestions,
        cityDescription: (state as IAuthState).SignUp.birthData.birthPlace.cityState.cityDescription,
        keyDown: (KEYS_WHITELIST.indexOf((state as IAuthState).SignUp.keyDown.key) !== -1) ? (state as IAuthState).SignUp.keyDown : ''
    } :
    {
        reset: state.reset,
        error: state.error,
        place: state.place,
        cities: state.cities,
        touched: state.touched,
        keyDown: state.keyDown,
        loading: state.loading,
        hoverItem: state.hoverItem,
        showSugestions: state.showSugestions,
        cityDescription: state.cityDescription,
        placeholderBirthPlace: state.placeholderBirthPlace
    }
);

export default connectAuth(mapStateToProps)(CitySelector);
